// src/pages/MembersListPage.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { Select } from "../../../components/catalyst/select";
import { Badge } from "../../../components/catalyst/badge";
import { getStatusBadgeClass } from "../../../util/statusBadge";

type ReportStatus = "pending" | "blocked" | "failed" | "draft" | "in-review" | "ready" | "sent";
type Report = { id: string; title: string; updatedAt: string; amount: number; status: ReportStatus };
type Member = {
  id: string;
  name: string;
  lastVisitedISO: string;
  reports: Report[];
};

const STATUS_FILTERS: Array<"All" | Exclude<ReportStatus, "in-review">> = [
  "All",
  "pending",
  "blocked",
  "failed",
  "draft",
  "ready",
  "sent",
];

const sorters = {
  recentDesc: (a: Member, b: Member) => +new Date(b.lastVisitedISO) - +new Date(a.lastVisitedISO),
  recentAsc: (a: Member, b: Member) => +new Date(a.lastVisitedISO) - +new Date(b.lastVisitedISO),
  nameAsc: (a: Member, b: Member) => a.name.localeCompare(b.name),
  nameDesc: (a: Member, b: Member) => b.name.localeCompare(a.name),
};

const badge = (s: ReportStatus) => {
  const base = "px-2 py-0.5 rounded-full text-xs font-medium";
  switch (s) {
    case "draft": return `${base} bg-amber-50 text-amber-700 border border-amber-200`;
    case "in-review": return `${base} bg-indigo-50 text-indigo-700 border border-indigo-200`;
    case "ready": return `${base} bg-violet-50 text-violet-700 border border-violet-200`;
    case "sent": return `${base} bg-emerald-50 text-emerald-700 border border-emerald-200`;
    case "blocked": return `${base} bg-slate-50 text-slate-700 border border-slate-200`;
    case "failed": return `${base} bg-rose-50 text-rose-700 border border-rose-200`;
    case "pending": return `${base} bg-blue-50 text-blue-700 border border-blue-200`;
  }
};

async function fetchAllUserReports() {
  const fn = httpsCallable(functions, "workflowFetchAllUserReports");
  const result = await fn({ action: "all" });
  return result.data as { count: number; data: any[] };
}

export default function MembersView() {

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(
    searchParams.get("q") ?? ""
  );
  const [filter, setFilter] = useState<(typeof STATUS_FILTERS)[number]>(
    (searchParams.get("f") as any) ?? "All"
  );
  const [sort, setSort] = useState<keyof typeof sorters>(
    (searchParams.get("s") as any) ?? "recentDesc"
  );
  const [page, setPage] = useState(
    Number(searchParams.get("p") ?? 1)
  );

  
  const [members, setMembers] = useState<Member[]>([]);
 
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  // Pagination state
  
  const pageSize = 10;

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchAllUserReports();
        const grouped: Record<string, Member> = {};

        console.log("res", res);

        res.data.forEach((entry) => {
          const u = entry.user;
          const r = entry.report;
          const t = entry.template;

          if (!grouped[u.id]) {
            grouped[u.id] = {
              id: u.id,
              name: u.fullName,
              lastVisitedISO: r.createdAt ?? new Date().toISOString(),
              reports: [],
            };
          }

          
          grouped[u.id].reports.push({
            id: r.id,
            title: t.title,
            updatedAt: r.updatedAt,
            amount: r?.amount || 0,
            status: (r.status?.toLowerCase() as ReportStatus) || "draft",
          });
        });

        setMembers(Object.values(grouped));
      } catch (e) {
        console.error("❌ fetchAllUserReports ERROR:", e);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  // Reset page when filters/search change
  useEffect(() => {
    const params = new URLSearchParams();
  
    // search text → q
    if (search.trim()) {
      params.set("q", search.trim());
    }
  
    // chip filter → f
    if (filter !== "All") {
      params.set("f", filter);
    }
  
    // sort dropdown → s
    if (sort !== "recentDesc") {
      params.set("s", sort);
    }
  
    // pagination → p
    if (page > 1) {
      params.set("p", String(page));
    }
  
    setSearchParams(params, { replace: true });
  }, [search, filter, sort, page]);
  

  const data = useMemo(() => {
    const term = search.trim().toLowerCase();

    const filtered = members
      .map((m) => ({
        ...m,
        reports:
          filter === "All"
            ? m.reports
            : m.reports.filter((r) => r.status.toLowerCase() === filter.toLowerCase()),
      }))
      .filter((m) => (!term ? true : m.name.toLowerCase().includes(term)))
      .filter((m) => m.reports.length > 0)
      .sort(sorters[sort]);

    const total = filtered.length;
    const pageCount = Math.ceil(total / pageSize);
    const start = (page - 1) * pageSize;
    const list = filtered.slice(start, start + pageSize);

    return { list, total, pageCount };
  }, [search, filter, sort, members, page]);

  const clickOnreport = (status: string, id: string) => {
    const allowedStatuses = ["draft", "ready", "sent", "in_review", "failed", 'outdated'];
    if (!allowedStatuses.includes(status)) return;
  
    const params = new URLSearchParams();
    params.set("from", "member-list");
  
    if (search.trim()) params.set("q", search.trim());
    if (filter !== "All") params.set("f", filter);
    if (sort !== "recentDesc") params.set("s", sort);
    if (page > 1) params.set("p", String(page));
  
    nav(`/admin/report/${id}?${params.toString()}`);
  };

  function formatDOB(dob?: string | null): string {
    if (!dob) return "";
  
    // Parse manually to avoid timezone shift
    const [year, month, day] = dob.split("-").map(Number);
  
    if (!year || !month || !day) return dob;
  
    const date = new Date(year, month - 1, day); // local date, no UTC shift
  
    return date.toLocaleDateString("en", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  return (
    <div className="mx-8 my-6">
      <h1 className="mb-3 text-xl font-semibold text-slate-800">Members</h1>

      {/* Controls */}
      <div className="mb-4 flex flex-col items-start gap-5 overflow-hidden">
        <div className="flex-none relative w-[65%]">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <svg className="h-4 w-4" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={2}>
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by member's name..."
            className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>

        <div className="flex items-center gap-5 w-[65%]">
          <div className="flex-1 min-w-0">
            <div className="flex items-center whitespace-nowrap gap-2 pr-2">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`shrink-0 rounded-full border px-3 py-1 text-sm ${
                    filter === s
                      ? "bg-brand-600 text-white border-brand-600"
                      : "bg-slate-100 text-slate-700 border-slate-300"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <Select value={sort} onChange={(e) => setSort(e.target.value as any)} className="select-padding flex-none shrink-0 max-w-48 text-sm">
            <option value="recentDesc">Most recent first</option>
            <option value="recentAsc">Oldest first</option>
            <option value="nameAsc">Name A–Z</option>
            <option value="nameDesc">Name Z–A</option>
          </Select>
        </div>
      </div>

      {/* LIST */}
      <div className="relative min-h-[320px]">
        {loading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white/60 backdrop-blur-sm z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
          </div>
        )}

        <div className="space-y-4 opacity-100">
          {data.list.map((m) => (
            <div key={m.id}  className="rounded-xl border border-slate-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="grid h-8 w-8 place-items-center rounded-full bg-brand-100 text-brand-700 font-medium">
                    {m.name[0]}
                  </div>
                  <div className="font-medium text-slate-800">{m.name}</div>
                </div>

                <Link
                  to={`/admin/members/view/${m.id}`} 
                  className="text-sm font-medium text-brand-700 hover:underline inline-flex items-center gap-1"
                >
                  View profile
                  <svg className="h-4 w-4" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </Link>
              </div>

              <div className="space-y-2">
                {m.reports.map((r) => (
                  <div key={r.id} onClick={() => clickOnreport(r.status, r.id)} className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2 !cursor-pointer transition-all duration-200 ease-in-out
                      hover:bg-brand-softer hover:scale-[1.02]
                      active:scale-[0.98]">
                    <div className="text-sm text-slate-800 ">{r.title}</div>
                    <div className="flex items-center gap-3 text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <circle cx="12" cy="12" r="9" /><path d="M12 7v6l4 2" />
                        </svg>
                        {formatDOB(r.updatedAt)}
                      </span>
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-gray-700 border border-emerald-200">
                        ${r.amount}
                      </span>
                      {/* <span className={badge()}>{r.status}</span> */}
                      <Badge
                        className={`${getStatusBadgeClass(
                          (r.status).toUpperCase()
                        )} capitalize bold text-xs px-2 py-0.5`}
                      >
                        {r.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!loading && data.total === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-slate-500">
              No results
            </div>
          )}
        </div>

        {/* PAGINATION */}
        {data.total > 0 && (
          <div className="mt-6 flex items-center justify-center gap-4">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className={`px-3 py-1 border rounded-md text-sm ${
                page === 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-100"
              }`}
            >
              Previous
            </button>

            <span className="text-sm text-slate-600">
              Page {page} of {data.pageCount}
            </span>

            <button
              disabled={page === data.pageCount}
              onClick={() => setPage((p) => p + 1)}
              className={`px-3 py-1 border rounded-md text-sm ${
                page === data.pageCount ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-100"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
